import { IPagination } from '@/types/general.type';
import { ApiResponse, axiosInstance } from '@/lib/axios';
import { QueryConfig } from '@/lib/react-query';
import { queryOptions, useQuery } from '@tanstack/react-query';
import { TTeam } from '../types/team.type';
import { TFetchParams } from '@/types/fetch.type';

export type TGetListTeamResponse = ApiResponse<IPagination<TTeam>>;
export type TGetListTeamParams = TFetchParams;

export const getListTeam = async (params: TGetListTeamParams): Promise<TGetListTeamResponse> => {
  const url = 'team/manage';

  return axiosInstance.get(url, { params });
};

export const GET_LIST_TEAM_QUERY_KEY = 'get-list-team';

export const getListTeamQueryOptions = <TData = IPagination<TTeam>>(
  params: TGetListTeamParams,
  select?: (data: TGetListTeamResponse) => TData
) => {
  return queryOptions({
    queryKey: [
      GET_LIST_TEAM_QUERY_KEY,
      {
        search: params?.search,
        page: params?.page,
        limit: params?.limit,
        sortDir: params?.sortDir,
        sortBy: params?.sortBy,
      },
    ],
    queryFn: () => getListTeam(params),
    select,
  });
};

// Overload when select is provided - returns transformed type
export function useGetListTeamQuery<TData>(options: {
  params: TGetListTeamParams;
  queryConfig?: QueryConfig<typeof getListTeamQueryOptions<TData>>;
  select: (data: TGetListTeamResponse) => TData;
}): ReturnType<typeof useQuery<TData>>;

// Overload when select is not provided - returns raw API response (even if generic is provided)
export function useGetListTeamQuery(options: {
  params: TGetListTeamParams;
  queryConfig?: QueryConfig<typeof getListTeamQueryOptions<TGetListTeamResponse>>;
}): ReturnType<typeof useQuery<TGetListTeamResponse>>;

// Implementation
export function useGetListTeamQuery<TData = TGetListTeamResponse>({
  queryConfig,
  params,
  select,
}: {
  params: TGetListTeamParams;
  queryConfig?: QueryConfig<typeof getListTeamQueryOptions<TData | TGetListTeamResponse>>;
  select?: (data: TGetListTeamResponse) => TData;
}) {
  return useQuery({
    ...getListTeamQueryOptions(params, select),
    ...queryConfig,
  }) as ReturnType<typeof useQuery<TData extends TGetListTeamResponse ? TGetListTeamResponse : TData>>;
}
